import React, { ReactNode, Suspense } from "react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { reportError } from "@app/services/error-reporter";

/**
 * Мета-настройки глобальной обработки ошибок в TanStack Query.
 */
interface GlobalErrorMeta {
    /** Если `true`, глобальный обработчик не покажет toast. */
    suppressGlobalError?: boolean;
    /** Человекочитаемый источник ошибки. */
    errorSource?: string;
}

type QueryMeta = GlobalErrorMeta & Record<string, unknown>;

/**
 * Приводит `mutationKey` к строке для fallback-источника ошибки.
 * @param mutationKey ключ мутации
 */
function stringifyMutationKey(mutationKey: readonly unknown[] | undefined): string {
    if (mutationKey == null || mutationKey.length === 0) {
        return "unknown";
    }
    return mutationKey.map((part) => String(part)).join(".");
}

declare module "@tanstack/react-query" {
    interface Register {
        queryMeta: QueryMeta;
        mutationMeta: QueryMeta;
    }
}

/**
 * Основной `QueryClient` приложения с глобальным перехватом ошибок query/mutation.
 */
export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            if (query.meta?.suppressGlobalError === true) {
                return;
            }
            reportError(error, {
                from: query.meta?.errorSource ?? `query:${query.queryHash}`,
                context: `queryKey=${query.queryHash}`,
            });
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            if (mutation.meta?.suppressGlobalError === true) {
                return;
            }

            const mutationKey = stringifyMutationKey(mutation.options.mutationKey);
            reportError(error, {
                from: mutation.meta?.errorSource ?? `mutation:${mutationKey}`,
                context: {
                    mutationKey: mutation.options.mutationKey,
                },
            });
        },
    }),
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
        },
    },
});

/**
 * Подключает `QueryClientProvider` и devtools (только в dev).
 */
export function QueryProvider({ children }: { children: ReactNode }) {
    return (
        <Suspense fallback="">
            <QueryClientProvider client={queryClient}>
                {children}
                {process.env.NODE_ENV !== "production" && (
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
                )}
            </QueryClientProvider>
        </Suspense>
    );
}
